/**
 * Notizen und Aufgaben je Konto (Migration 162, `hetzner/webhook/notiz_store.py`).
 *
 * Eine Notiz ist freier Text mit Bezug; abhakbar sind ihre Punkte. Alle
 * Kontor-Konten sehen alle Notizen; wer sie erledigt, sagt `zustaendig`
 * (Migration 165). Konzept: docs/konzepte/KONZEPT-NOTIZEN-AUFGABEN.md
 */

export type NotizBezug = {
  kunde_id: string;
  projekt_id: string;
  lieferant_firma_id: string;
  termin_id: string;
  anfrage_id: string;
};

/** Beim Anlegen: nur die Felder, die feststehen. Der Server ergänzt Kunde und Projekt selbst. */
export type NotizBezugTeil = Partial<NotizBezug>;

export type NotizPunkt = {
  id: string;
  text: string;
  reihenfolge: number;
  /** Leer = offen. */
  erledigt_am: string;
  created_at: string;
};

export type Kontakt = { name: string; rolle: string; email: string; telefon: string };

export type NotizKontext = {
  kunde: { id: string; name: string; email: string; telefon: string; adresse: string; kontakte: Kontakt[] } | null;
  projekt: { id: string; titel: string; status: string } | null;
  lieferant: { id: string; name: string; kontakte: Kontakt[] } | null;
  termin: { id: string; titel: string; start_at: string; besichtigung: boolean } | null;
  anfrage: { id: string; name: string; email: string; telefon: string; betreff: string; status: string } | null;
};

export type Notiz = {
  id: string;
  text: string;
  bezug: NotizBezug;
  /** Leer = nicht abgelegt. */
  abgelegt_am: string;
  /** Zurückgestellt bis (Migration 213) — leer, wenn nicht oder die Zeit schon um ist. */
  zurueckgestellt_bis?: string;
  /** Flagge „wichtig" (Migration 221) — steht ganz oben. */
  wichtig?: boolean;
  created_at: string;
  updated_at: string;
  /** Nicht abgelegt und — sofern es Punkte gibt — mindestens einer offen. */
  offen: boolean;
  punkte: NotizPunkt[];
  /** Wer die Aufgabe erledigt; `ich` = das angemeldete Konto. */
  zustaendig: { id: number; name: string; ich: boolean };
  /** Wer die Notiz angelegt hat. */
  urheber: { id: number; name: string };
  kontext: NotizKontext;
  /** Mailadressen, die zu Kunde, Leed und Lieferant gehören — für „Mail schreiben". */
  adressen: { email: string; wer: string }[];
  /** Aufgabe „Bewertung erbitten" (Migration 192) — sonst null. */
  bewertung?: { id: string; status: "offen" | "gesendet" | "verzichtet" } | null;
  /** Phasenaufgabe (Plan Projekt-Tracking, Etappe 4) — vom Postlauf angelegt, sonst null. */
  /** Lage, Thema und Grund, gerechnet auf dem Server (`aufgaben_ordnung.py`, 12.10.2026). */
  ordnung?: AufgabenOrdnung;
  phase_aufgabe?: {
    regel: "planen" | "nachhaken" | "zahlungserinnerung" | "zahlung_pruefen" | "helferrechnung";
    nummer: string;
    /** Nur bei „zahlung_pruefen": die Bankzeile, die vermutlich zur Rechnung gehört. */
    kontobewegung_id?: string;
  } | null;
};

export type AufgabenThema = "geld" | "angebote" | "planung" | "bewertungen" | "notizen";
/** Gruppe oben: kommende Termine (7 Tage) oder überfällig — sonst leer (`aufgaben_ordnung.py`). */
export type AufgabenLage = "termin" | "ueberfaellig" | "";
export type AufgabenOrdnung = { thema: AufgabenThema; lage: AufgabenLage; grund: string; rang: number };

export type NotizStatus = "offen" | "zurueckgestellt" | "fertig" | "alle";

/** Wessen Aufgaben: „meine", „alle" oder eine Konto-ID als Text. */
export type NotizWer = string;

export type Person = { id: number; name: string; ich: boolean };

export type NotizNeu = { text: string; bezug: NotizBezugTeil; punkte: string[]; zustaendig_id?: number };

/** Vorname für Filter und Zuständigkeit — „Volker" statt „Volker Croy". */
export function vorname(name: string): string {
  return (name || "").trim().split(/\s+/)[0] || name;
}

/** Die eine Nummer, die „Anrufen" wählt: Kunde, sonst Leed, sonst erster Kontakt. */
export function hauptnummer(k: NotizKontext): string {
  return k.kunde?.telefon
    || k.anfrage?.telefon
    || k.kunde?.kontakte.find((c) => c.telefon)?.telefon
    || k.lieferant?.kontakte.find((c) => c.telefon)?.telefon
    || "";
}

/**
 * Aus einem Notizfeld Text und Punkte machen: Zeilen, die mit „-", „*", „[ ]"
 * oder „☐" beginnen, werden Punkte, der Rest bleibt Notiz. Eine einzelne Zeile
 * ohne Strich ist eine Aufgabe. So lässt sich eine
 * Besichtigung in einem Rutsch diktieren, ohne zwischen Feldern zu springen.
 */
export function zerlegen(roh: string): { text: string; punkte: string[] } {
  const text: string[] = [];
  const punkte: string[] = [];
  for (const zeile of roh.split("\n")) {
    const treffer = zeile.match(/^\s*(?:[-*•]|\[\s?\]|☐)\s+(.*\S)\s*$/);
    if (treffer) punkte.push(treffer[1].slice(0, 300));
    else text.push(zeile);
  }
  const rest = text.join("\n").trim();
  // Eine einzelne Zeile ohne Strich ist eine Aufgabe, keine Notiz — „Rückruf Frau Müller" soll abhakbar sein.
  if (punkte.length === 0 && rest && !rest.includes("\n") && rest.length <= 300) return { text: "", punkte: [rest] };
  return { text: rest, punkte };
}

/**
 * Überschrift einer Notiz — das Wichtigste aus dem Bezug, in fester Reihenfolge.
 * `ohne` lässt den Namen weg, der schon als Gruppenüberschrift darübersteht.
 */
export function bezugTitel(n: Pick<Notiz, "kontext">, ohne = ""): string {
  const k = n.kontext;
  const teile = [
    k.kunde?.name || k.anfrage?.name || "",
    k.projekt?.titel || "",
    k.lieferant?.name || "",
  ].filter((t) => t && t !== ohne);
  return teile.join(" · ") || (ohne ? "" : "Ohne Bezug");
}

/** Schlüssel zum Gruppieren auf der Aufgabenseite: Kunde vor Leed vor Lieferant. */
export function gruppenSchluessel(n: Notiz): { schluessel: string; titel: string; art: string } {
  const k = n.kontext;
  if (k.kunde) return { schluessel: "k:" + k.kunde.id, titel: k.kunde.name, art: "Kunde" };
  if (k.anfrage) return { schluessel: "a:" + k.anfrage.id, titel: k.anfrage.name || k.anfrage.betreff, art: "Leed" };
  if (k.lieferant) return { schluessel: "l:" + k.lieferant.id, titel: k.lieferant.name, art: "Lieferant" };
  if (k.projekt) return { schluessel: "p:" + k.projekt.id, titel: k.projekt.titel, art: "Projekt" };
  return { schluessel: "-", titel: "Ohne Bezug", art: "" };
}

/** `tel:`-Link: nur Ziffern und ein führendes Plus, sonst wählt das Telefon falsch. */
export function telLink(nummer: string): string {
  const s = (nummer || "").trim();
  return "tel:" + (s.startsWith("+") ? "+" : "") + s.replace(/[^\d]/g, "");
}

export function kartenLink(adresse: string): string {
  return "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(adresse);
}

export function datumDe(iso: string, mitZeit = false): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleString("de-DE", mitZeit
    ? { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "2-digit", year: "2-digit" });
}

/** Was eine Aufgabe verlangt — danach hebt die Karte den passenden Knopf hervor. */
export type Tat = "angebot" | "rechnung" | "termin" | "anruf" | "mail";

/*
 * Reihenfolge ist Absicht: „Angebot schreiben" ist ein Angebot, keine Mail;
 * „Rechnung schicken" eine Rechnung. Erst danach Termin, Anruf, Mail.
 */
const TAT_MUSTER: [Tat, RegExp][] = [
  ["angebot", /angebot|kostenvoranschlag|kalkul/i],
  ["rechnung", /rechnung|abrechn|abschlag/i],
  ["termin", /termin|besichtig|vor ort|vorbeifahren|einplanen|vereinbar/i],
  ["anruf", /anruf|anrufen|rückruf|zurückruf|telefon|klingeln/i],
  ["mail", /mail|schreiben|anfrag|nachfrag|nachhak|senden|schicken|bestell|bescheid|zusage|absage|erinner/i],
];

/**
 * Die Tat, die zur Aufgabe passt: gelesen aus dem ersten offenen Punkt, sonst
 * aus dem Notiztext. Ein Wort im Text, keine Deutung — trifft nichts, bleibt
 * es bei den normalen Knöpfen.
 */
export function passendeTat(n: Pick<Notiz, "punkte" | "text">): Tat | null {
  const offen = n.punkte.filter((p) => !p.erledigt_am).map((p) => p.text);
  for (const quelle of [...offen, n.text]) {
    for (const [tat, muster] of TAT_MUSTER) if (muster.test(quelle || "")) return tat;
  }
  return null;
}

/** Vorschlag für den Projektnamen, wenn aus der Notiz ein Angebot mit neuem Projekt wird. */
export function projektTitelVorschlag(n: Notiz): string {
  const zeile = (n.text || "").split("\n").map((z) => z.trim()).find(Boolean);
  const titel = n.kontext.termin && !/^besichtigung$/i.test(n.kontext.termin.titel) ? n.kontext.termin.titel : "";
  return (titel || zeile || "").slice(0, 80);
}
